import {
  type EnvironmentId,
  type LiveSubagent,
  type LiveSubagentActivityEvent,
  type LiveSubagentActivityItem,
  type LiveSubagentRosterEvent,
  type SubagentObservationFacets,
  type ServerConfig,
  type ServerConfigStreamEvent,
  type ServerLifecycleWelcomePayload,
  WS_METHODS,
} from "@t3tools/contracts";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Queue from "effect/Queue";
import * as Ref from "effect/Ref";
import * as Result from "effect/Result";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";
import { AsyncResult, Atom } from "effect/unstable/reactivity";

import {
  createAtomCommandScheduler,
  createEnvironmentRpcCommand,
  createEnvironmentRpcQueryAtomFamily,
  createEnvironmentRpcSubscriptionAtomFamily,
} from "./runtime.ts";
import type { EnvironmentRegistry } from "../connection/registry.ts";
import { EnvironmentSupervisor } from "../connection/supervisor.ts";
import { safeErrorLogAttributes } from "../errors/safeLog.ts";
import { EnvironmentCacheStore } from "../platform/persistence.ts";
import { subscribe, type EnvironmentRpcInput } from "../rpc/client.ts";
import { followStreamInEnvironment } from "./runtime.ts";

export interface ServerConfigProjection {
  readonly config: ServerConfig;
  readonly latestEvent: ServerConfigStreamEvent;
  readonly source: "cache" | "live";
}

export interface LiveSubagentRosterState {
  readonly status: "available" | "unavailable" | "expired";
  readonly observationKey: string | null;
  readonly agents: ReadonlyArray<LiveSubagent>;
  readonly facets: SubagentObservationFacets | null;
  readonly reason?: Extract<LiveSubagentRosterEvent, { type: "unavailable" }>["reason"];
}

export interface LiveSubagentTimelineState {
  readonly status: "available" | "unavailable" | "expired";
  readonly observationKey: string | null;
  readonly items: ReadonlyArray<LiveSubagentActivityItem>;
  readonly truncated: boolean;
  readonly reason?: Extract<LiveSubagentActivityEvent, { type: "unavailable" }>["reason"];
}

const EMPTY_LIVE_SUBAGENT_ROSTER: LiveSubagentRosterState = {
  status: "available",
  observationKey: null,
  agents: [],
  facets: null,
};
const EMPTY_LIVE_SUBAGENT_TIMELINE: LiveSubagentTimelineState = {
  status: "available",
  observationKey: null,
  items: [],
  truncated: false,
};
const LIVE_SUBAGENT_TIMELINE_LIMIT = 512;
const LIVE_SUBAGENT_ITEM_TEXT_LIMIT = 128 * 1024;
const TERMINAL_LIVE_SUBAGENT_PHASES = new Set(["completed", "failed", "stopped"]);

function boundActivityItem(item: LiveSubagentActivityItem): LiveSubagentActivityItem {
  if (item.kind === "text" || item.kind === "reasoning") {
    return { ...item, content: item.content.slice(-LIVE_SUBAGENT_ITEM_TEXT_LIMIT) };
  }
  if (item.kind === "lifecycle") {
    return item.lifecycle.summary
      ? {
          ...item,
          lifecycle: {
            ...item.lifecycle,
            summary: item.lifecycle.summary.slice(-LIVE_SUBAGENT_ITEM_TEXT_LIMIT),
          },
        }
      : item;
  }
  return {
    ...item,
    tool: {
      ...item.tool,
      ...(item.tool.input ? { input: item.tool.input.slice(-LIVE_SUBAGENT_ITEM_TEXT_LIMIT) } : {}),
      ...(item.tool.output
        ? { output: item.tool.output.slice(-LIVE_SUBAGENT_ITEM_TEXT_LIMIT) }
        : {}),
      ...(item.tool.error ? { error: item.tool.error.slice(-LIVE_SUBAGENT_ITEM_TEXT_LIMIT) } : {}),
    },
  };
}

export function projectLiveSubagentRoster(
  current: LiveSubagentRosterState,
  event: LiveSubagentRosterEvent,
): readonly [LiveSubagentRosterState, ReadonlyArray<LiveSubagentRosterState>] {
  let next: LiveSubagentRosterState;
  if (event.type === "snapshot") {
    next = {
      status: "available",
      observationKey: event.observationKey,
      agents: [...event.agents],
      facets: event.facets,
    };
  } else if (event.type === "upsert") {
    if (current.observationKey !== event.observationKey) return [current, []];
    const agents = new Map(current.agents.map((agent) => [agent.id, agent]));
    const previous = agents.get(event.agent.id);
    const rejectStatusRegression =
      previous !== undefined &&
      TERMINAL_LIVE_SUBAGENT_PHASES.has(previous.phase) &&
      !TERMINAL_LIVE_SUBAGENT_PHASES.has(event.agent.phase);
    agents.set(
      event.agent.id,
      rejectStatusRegression ? { ...event.agent, phase: previous.phase } : event.agent,
    );
    next = {
      status: "available",
      observationKey: event.observationKey,
      agents: Array.from(agents.values()),
      facets: current.facets,
    };
  } else if (event.type === "expired") {
    if (current.observationKey !== event.observationKey) return [current, []];
    next = { status: "expired", observationKey: null, agents: [], facets: null };
  } else {
    if (!("observationKey" in event)) {
      if (current.observationKey !== null) return [current, []];
      next = {
        status: "unavailable",
        observationKey: null,
        agents: [],
        facets: null,
        reason: event.reason,
      };
    } else {
      if (current.observationKey !== null && current.observationKey !== event.observationKey) {
        return [current, []];
      }
      next = {
        status: "unavailable",
        observationKey: event.observationKey,
        agents: [],
        facets: null,
        reason: event.reason,
      };
    }
  }
  return [next, [next]];
}

export function projectLiveSubagentTimeline(
  current: LiveSubagentTimelineState,
  event: LiveSubagentActivityEvent,
): readonly [LiveSubagentTimelineState, ReadonlyArray<LiveSubagentTimelineState>] {
  let next: LiveSubagentTimelineState;
  if (event.type === "snapshot") {
    next = {
      status: "available",
      observationKey: event.observationKey,
      items: event.items.slice(-LIVE_SUBAGENT_TIMELINE_LIMIT).map(boundActivityItem),
      truncated: event.truncated || event.items.length > LIVE_SUBAGENT_TIMELINE_LIMIT,
    };
  } else if (event.type === "append") {
    if (current.observationKey !== null && current.observationKey !== event.observationKey) {
      return [current, []];
    }
    const items = [...current.items];
    const existingIndex = items.findIndex((item) => item.id === event.itemId);
    if (existingIndex === -1) {
      items.push({
        id: event.itemId,
        kind: event.kind,
        createdAt: event.createdAt,
        content: event.delta.slice(-LIVE_SUBAGENT_ITEM_TEXT_LIMIT),
        complete: false,
      });
    } else {
      const existing = items[existingIndex]!;
      const existingContent =
        existing.kind === "text" || existing.kind === "reasoning" ? existing.content : "";
      items[existingIndex] = {
        id: event.itemId,
        kind: event.kind,
        createdAt: existing.createdAt,
        complete: false,
        content: `${existingContent}${event.delta}`.slice(-LIVE_SUBAGENT_ITEM_TEXT_LIMIT),
      };
    }
    next = {
      status: "available",
      observationKey: event.observationKey,
      items: items.slice(-LIVE_SUBAGENT_TIMELINE_LIMIT),
      truncated: current.truncated || items.length > LIVE_SUBAGENT_TIMELINE_LIMIT,
    };
  } else if (event.type === "upsert") {
    if (current.observationKey !== null && current.observationKey !== event.observationKey) {
      return [current, []];
    }
    const items = [...current.items];
    const existingIndex = items.findIndex((item) => item.id === event.item.id);
    if (existingIndex === -1) items.push(boundActivityItem(event.item));
    else {
      const existing = items[existingIndex]!;
      if (
        existing.kind === "tool" &&
        event.item.kind === "tool" &&
        existing.tool &&
        event.item.tool
      ) {
        const input = event.item.tool.input ?? existing.tool.input;
        const output = event.item.tool.output ?? existing.tool.output;
        items[existingIndex] = boundActivityItem({
          ...existing,
          ...event.item,
          createdAt: existing.createdAt,
          tool: {
            ...existing.tool,
            ...event.item.tool,
            name: event.item.tool.name === "tool" ? existing.tool.name : event.item.tool.name,
            ...(input === undefined ? {} : { input }),
            ...(output === undefined ? {} : { output }),
          },
        });
      } else {
        items[existingIndex] = boundActivityItem(event.item);
      }
    }
    next = {
      status: "available",
      observationKey: event.observationKey,
      items: items.slice(-LIVE_SUBAGENT_TIMELINE_LIMIT),
      truncated: current.truncated || items.length > LIVE_SUBAGENT_TIMELINE_LIMIT,
    };
  } else if (event.type === "expired") {
    if (current.observationKey !== event.observationKey) return [current, []];
    next = { status: "expired", observationKey: null, items: [], truncated: false };
  } else {
    if (current.observationKey !== null && current.observationKey !== event.observationKey) {
      return [current, []];
    }
    next = {
      status: "unavailable",
      observationKey: event.observationKey,
      items: [],
      truncated: false,
      reason: event.reason,
    };
  }
  return [next, [next]];
}

export function applyServerConfigProjection(
  current: Option.Option<ServerConfigProjection>,
  event: ServerConfigStreamEvent,
): Option.Option<ServerConfigProjection> {
  switch (event.type) {
    case "snapshot":
      return Option.some({
        config: event.config,
        latestEvent: event,
        source: "live",
      });
    case "keybindingsUpdated":
      return Option.map(current, (projection) => ({
        config: {
          ...projection.config,
          keybindings: event.payload.keybindings,
          issues: event.payload.issues,
        },
        latestEvent: event,
        source: "live",
      }));
    case "providerStatuses":
      return Option.map(current, (projection) => ({
        config: {
          ...projection.config,
          providers: event.payload.providers,
        },
        latestEvent: event,
        source: "live",
      }));
    case "settingsUpdated":
      return Option.map(current, (projection) => ({
        config: {
          ...projection.config,
          settings: event.payload.settings,
        },
        latestEvent: event,
        source: "live",
      }));
  }
}

export function projectServerConfig(
  current: Option.Option<ServerConfigProjection>,
  event: ServerConfigStreamEvent,
): readonly [Option.Option<ServerConfigProjection>, ReadonlyArray<ServerConfigProjection>] {
  const next = applyServerConfigProjection(current, event);
  return [next, Option.toArray(next)];
}

const cachedConfigSnapshotEvent = (config: ServerConfig): ServerConfigStreamEvent => ({
  version: 1,
  type: "snapshot",
  config,
});

/**
 * Keeps a complete server configuration available during reconnects. Server
 * config carries the provider/model catalogue used by task creation, so it is
 * useful—and safe—to retain after a transport session ends.
 */
export const makeEnvironmentServerConfigState = Effect.fn("EnvironmentServerConfigState.make")(
  function* () {
    const supervisor = yield* EnvironmentSupervisor;
    const cache = yield* EnvironmentCacheStore;
    const environmentId = supervisor.target.environmentId;
    const cachedConfig = yield* cache.loadServerConfig(environmentId).pipe(
      Effect.catch((error) =>
        Effect.logWarning("Could not load cached server configuration.").pipe(
          Effect.annotateLogs({
            environmentId,
            ...safeErrorLogAttributes(error),
          }),
          Effect.as(Option.none<ServerConfig>()),
        ),
      ),
    );
    const state = yield* SubscriptionRef.make<Option.Option<ServerConfigProjection>>(
      Option.map(cachedConfig, (config) => ({
        config,
        latestEvent: cachedConfigSnapshotEvent(config),
        source: "cache" as const,
      })),
    );
    const persistence = yield* Queue.sliding<ServerConfig>(1);
    const pendingPersistence = yield* Ref.make<Option.Option<ServerConfig>>(Option.none());

    const persist = Effect.fn("EnvironmentServerConfigState.persist")(function* (
      config: ServerConfig,
    ) {
      return yield* cache.saveServerConfig(environmentId, config).pipe(
        Effect.as(true),
        Effect.catch((error) =>
          Effect.logWarning("Could not persist cached server configuration.").pipe(
            Effect.annotateLogs({
              environmentId,
              ...safeErrorLogAttributes(error),
            }),
            Effect.as(false),
          ),
        ),
      );
    });

    const persistPending = Effect.fn("EnvironmentServerConfigState.persistPending")(function* (
      config: ServerConfig,
    ) {
      if (!(yield* persist(config))) {
        return;
      }
      yield* Ref.update(pendingPersistence, (pending) =>
        Option.isSome(pending) && pending.value === config ? Option.none() : pending,
      );
    });

    yield* Stream.fromQueue(persistence).pipe(
      Stream.debounce("500 millis"),
      Stream.runForEach(persistPending),
      Effect.forkScoped,
    );

    yield* subscribe(WS_METHODS.subscribeServerConfig, {}).pipe(
      Stream.runForEach((event) =>
        Effect.gen(function* () {
          const next = applyServerConfigProjection(yield* SubscriptionRef.get(state), event);
          if (Option.isNone(next)) {
            return;
          }
          yield* Ref.set(pendingPersistence, Option.some(next.value.config));
          yield* SubscriptionRef.set(state, next);
          yield* Queue.offer(persistence, next.value.config);
        }),
      ),
      Effect.forkScoped,
    );

    yield* Effect.addFinalizer(() =>
      Ref.get(pendingPersistence).pipe(
        Effect.flatMap(
          Option.match({
            onNone: () => Effect.void,
            onSome: (config) => persist(config).pipe(Effect.asVoid),
          }),
        ),
      ),
    );

    return state;
  },
);

export function serverConfigStateChanges(environmentId: EnvironmentId) {
  return followStreamInEnvironment(
    environmentId,
    Stream.unwrap(
      makeEnvironmentServerConfigState().pipe(
        Effect.map((state) =>
          SubscriptionRef.changes(state).pipe(
            Stream.filterMap((projection) =>
              Option.match(projection, {
                onNone: () => Result.failVoid,
                onSome: (value) => Result.succeed(value),
              }),
            ),
          ),
        ),
      ),
    ),
  );
}

export function projectServerWelcome(
  current: Option.Option<ServerLifecycleWelcomePayload>,
  event: {
    readonly type: "welcome" | "ready";
    readonly payload: unknown;
  },
): readonly [
  Option.Option<ServerLifecycleWelcomePayload>,
  ReadonlyArray<ServerLifecycleWelcomePayload>,
] {
  if (event.type !== "welcome") {
    return [current, []];
  }
  const welcome = event.payload as ServerLifecycleWelcomePayload;
  return [Option.some(welcome), [welcome]];
}

export function resolveServerConfigValue(
  projection: ServerConfigProjection | null,
  initialConfig: ServerConfig | null,
): ServerConfig | null {
  if (projection?.source === "live") return projection.config;
  return initialConfig ?? projection?.config ?? null;
}

export function createServerEnvironmentAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | EnvironmentCacheStore | R, E>,
  options: {
    readonly initialConfigValueAtom: (
      environmentId: EnvironmentId,
    ) => Atom.Atom<ServerConfig | null>;
  },
) {
  const configScheduler = createAtomCommandScheduler();
  const configConcurrency = {
    mode: "serial" as const,
    key: ({ environmentId }: { readonly environmentId: string }) => environmentId,
  };
  const configProjectionFamily = Atom.family((environmentId: EnvironmentId) =>
    runtime
      .atom(serverConfigStateChanges(environmentId))
      .pipe(
        Atom.setIdleTTL(5 * 60_000),
        Atom.withLabel(`environment-data:server:config-projection:${environmentId}`),
      ),
  );
  const configProjection = (target: {
    readonly environmentId: EnvironmentId;
    readonly input: EnvironmentRpcInput<typeof WS_METHODS.subscribeServerConfig>;
  }) => configProjectionFamily(target.environmentId);
  const emptyConfigAtom = Atom.make<ServerConfig | null>(null).pipe(
    Atom.withLabel("environment-data:server:config:empty"),
  );
  const configValueAtom = Atom.family((environmentId: EnvironmentId | null) => {
    if (environmentId === null) {
      return emptyConfigAtom;
    }
    return Atom.make((get): ServerConfig | null => {
      const projection = Option.getOrNull(
        AsyncResult.value(get(configProjection({ environmentId, input: {} }))),
      );
      return resolveServerConfigValue(
        projection,
        get(options.initialConfigValueAtom(environmentId)),
      );
    }).pipe(Atom.withLabel(`environment-data:server:config:${environmentId}`));
  });
  const settingsValueAtom = Atom.family((environmentId: EnvironmentId) =>
    Atom.make((get) => get(configValueAtom(environmentId))?.settings ?? null).pipe(
      Atom.withLabel(`environment-data:server:settings:${environmentId}`),
    ),
  );
  const providersValueAtom = Atom.family((environmentId: EnvironmentId) =>
    Atom.make((get) => get(configValueAtom(environmentId))?.providers ?? null).pipe(
      Atom.withLabel(`environment-data:server:providers:${environmentId}`),
    ),
  );

  return {
    configValueAtom,
    settingsValueAtom,
    providersValueAtom,
    traceDiagnostics: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:server:trace-diagnostics",
      tag: WS_METHODS.serverGetTraceDiagnostics,
    }),
    processDiagnostics: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:server:process-diagnostics",
      tag: WS_METHODS.serverGetProcessDiagnostics,
    }),
    processResourceHistory: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:server:process-resource-history",
      tag: WS_METHODS.serverGetProcessResourceHistory,
    }),
    liveSubagentRoster: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:server:live-subagent-roster",
      tag: WS_METHODS.subscribeLiveSubagentRoster,
      idleTtlMs: 1_000,
      transform: (stream) =>
        stream.pipe(Stream.mapAccum(() => EMPTY_LIVE_SUBAGENT_ROSTER, projectLiveSubagentRoster)),
    }),
    liveSubagentTimeline: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:server:live-subagent-timeline",
      tag: WS_METHODS.subscribeLiveSubagentTimeline,
      idleTtlMs: 1_000,
      transform: (stream) =>
        stream.pipe(
          Stream.mapAccum(() => EMPTY_LIVE_SUBAGENT_TIMELINE, projectLiveSubagentTimeline),
        ),
    }),
    configProjection,
    welcome: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:server:welcome",
      tag: WS_METHODS.subscribeServerLifecycle,
      transform: (stream) =>
        stream.pipe(
          Stream.mapAccum(Option.none<ServerLifecycleWelcomePayload>, projectServerWelcome),
        ),
    }),
    refreshProviders: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:refresh-providers",
      tag: WS_METHODS.serverRefreshProviders,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId }) => environmentId,
      },
    }),
    updateProvider: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:update-provider",
      tag: WS_METHODS.serverUpdateProvider,
      scheduler: configScheduler,
      concurrency: configConcurrency,
    }),
    updateServer: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:update-server",
      tag: WS_METHODS.serverUpdateServer,
      scheduler: configScheduler,
      concurrency: configConcurrency,
    }),
    upsertKeybinding: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:upsert-keybinding",
      tag: WS_METHODS.serverUpsertKeybinding,
      scheduler: configScheduler,
      concurrency: configConcurrency,
    }),
    removeKeybinding: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:remove-keybinding",
      tag: WS_METHODS.serverRemoveKeybinding,
      scheduler: configScheduler,
      concurrency: configConcurrency,
    }),
    updateSettings: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:update-settings",
      tag: WS_METHODS.serverUpdateSettings,
      scheduler: configScheduler,
      concurrency: configConcurrency,
    }),
    signalProcess: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:signal-process",
      tag: WS_METHODS.serverSignalProcess,
    }),
  };
}
